/**
 * Shared candidate UI components extracted from CandidateDetailContent.
 * Both the modal (CandidateDetailContent) and the page (CandidateProfileCard /
 * CandidateProfileContent) import from here so that animations, ARIA patterns
 * and behaviour are consistent across both surfaces.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCandidateCv } from "../../hooks/useCandidateCv";
import { GradientSpinner } from "../common/GradientSpinner";
import DownloadMinimalisticLinear from "~icons/solar/download-minimalistic-linear";
import AltArrowLeftLinear from "~icons/solar/alt-arrow-left-linear";
import AltArrowRightLinear from "~icons/solar/alt-arrow-right-linear";
import MagnifierZoomInLinear from "~icons/solar/magnifer-zoom-in-linear";
import MagnifierZoomOutLinear from "~icons/solar/magnifer-zoom-out-linear";
import { useAnimatedScore } from "../../hooks/useAnimatedScore";
import type {
  CandidateSkill,
  EmploymentHistoryItem,
  EducationItem,
  CandidateStatusEnum,
} from "../../types/candidate";
import { STATUS_CONFIG, STATUS_STYLE_MAP } from "../../types/candidate";
import { cn, toTitleCase } from "../../lib/utils";
import { AiSparkleIcon } from "../ui/AiSparkleIcon";
import { Icon } from "../ui/Icon";
import SuitcaseLinear from "~icons/solar/suitcase-linear";
import CopyLinear from "~icons/solar/copy-linear";
import { MarkdownContent } from "../chat/MarkdownContent";
import {
  formatDate,
  getScoreColor,
  sortEmploymentByDate,
} from "./candidate-utils";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure pdf.js worker — use unpkg CDN matching the exact bundled version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ---------------------------------------------------------------------------
// CollapsibleContent — CSS grid-rows animation for smooth expand/collapse
// ---------------------------------------------------------------------------

export function CollapsibleContent({
  open,
  children,
  className,
}: {
  open: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`grid motion-safe:transition-[grid-template-rows] motion-safe:duration-300 motion-safe:ease-in-out ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      } ${className ?? ""}`}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG helpers (private to this module)
// ---------------------------------------------------------------------------

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      className={`shrink-0 motion-safe:transition-transform motion-safe:duration-200 ${open ? "rotate-180" : ""}`}
    >
      <path
        d="M4 6L8 10L12 6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GraduationIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        d="M8 2L1 6L8 10L15 6L8 2Z"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.5 7.5V11.5C3.5 11.5 5.25 13 8 13C10.75 13 12.5 11.5 12.5 11.5V7.5"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ScoreDisplay
// ---------------------------------------------------------------------------

export interface ScoreDisplayProps {
  label: string;
  score: number | undefined;
}

export function ScoreDisplay({ label, score }: ScoreDisplayProps) {
  const animatedValue = useAnimatedScore(score ?? 0);
  const hasScore = score !== undefined && score !== null;

  return (
    <div className="flex flex-row items-center justify-between">
      <span className="text-body-md font-medium text-black-500/60">
        {label}
      </span>
      {hasScore ? (
        <span
          className={`text-body-xl font-bold leading-tight tabular-nums ${getScoreColor(score)}`}
        >
          {animatedValue}%
        </span>
      ) : (
        <span className="text-body-xl font-bold leading-tight text-grey">
          -
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

export interface StatusBadgeProps {
  status: CandidateStatusEnum;
  /** When true, applies the background fill from STATUS_STYLE_MAP (page style). */
  showBackground?: boolean;
}

export function StatusBadge({
  status,
  showBackground = false,
}: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const label = config?.label ?? status;
  const style = STATUS_STYLE_MAP[config?.color ?? "grey"];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-4 py-1.5 text-body-md font-semibold",
        style.border,
        style.text,
        showBackground && style.bg
      )}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// TabBar — generic ARIA-compliant tab bar
// ---------------------------------------------------------------------------

export interface TabConfig {
  id: string;
  label: string;
}

export interface TabBarProps {
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (id: string) => void;
  /** aria-label for the tablist container. */
  ariaLabel?: string;
}

export function TabBar({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel = "Tabs",
}: TabBarProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = tabs.findIndex((t) => t.id === activeTab);
    let nextIndex = currentIndex;

    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    onTabChange(tabs[nextIndex].id);
  };

  return (
    <div
      className="flex items-center gap-1 rounded-full bg-bg-page p-1"
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              "rounded-full px-4 py-2 text-body-sm font-medium motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
              isActive
                ? "bg-secondary text-white"
                : "text-grey hover:text-text-primary"
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AnimatedSection — staggered entrance animation wrapper
// ---------------------------------------------------------------------------

export function AnimatedSection({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  return (
    <div
      className="section-stagger-in"
      style={{ "--stagger-delay": `${index * 75}ms` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkillChip
// ---------------------------------------------------------------------------

export function SkillChip({ skill }: { skill: CandidateSkill }) {
  return (
    <span className="rounded-full border border-grey-100 bg-grey-50 px-3 py-1.5 text-body-xs font-medium text-grey">
      {skill.name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// AttributesSection — collapsible skills/attributes list
// ---------------------------------------------------------------------------

const ATTRIBUTES_VISIBLE_COUNT = 6;

export interface AttributesSectionProps {
  /** Items with a `name` string property. Pass `{ name: attr }` for string arrays. */
  items: CandidateSkill[];
  /** How many chips to show before collapsing. Defaults to 6. */
  visibleCount?: number;
}

export function AttributesSection({
  items,
  visibleCount = ATTRIBUTES_VISIBLE_COUNT,
}: AttributesSectionProps) {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasOverflow = items.length > visibleCount;
  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);

  return (
    <div className="flex flex-col gap-2.5">
      {/* Section header — click to collapse/expand the whole section */}
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        onClick={() => setSectionOpen((prev) => !prev)}
        aria-expanded={sectionOpen}
      >
        <h4 className="text-body-xl font-semibold text-secondary">
          Attributes
        </h4>
        <ChevronIcon open={sectionOpen} />
      </button>

      <CollapsibleContent open={sectionOpen}>
        <div className="flex flex-col gap-2.5 pt-0.5">
          {items.length > 0 ? (
            <>
              <div className="flex flex-wrap gap-2">
                {visibleItems.map((skill) => (
                  <SkillChip key={skill.name} skill={skill} />
                ))}
              </div>

              {hasOverflow && (
                <>
                  <CollapsibleContent open={isExpanded}>
                    <div className="flex flex-wrap gap-2">
                      {overflowItems.map((skill) => (
                        <SkillChip key={skill.name} skill={skill} />
                      ))}
                    </div>
                  </CollapsibleContent>

                  <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="mt-1 flex cursor-pointer items-center gap-1 text-body-sm text-grey hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <span>
                      {isExpanded
                        ? "See less"
                        : `See more (${items.length - visibleCount})`}
                    </span>
                    <ChevronIcon open={isExpanded} />
                  </button>
                </>
              )}
            </>
          ) : (
            <p className="text-body-sm text-grey">No attributes available.</p>
          )}
        </div>
      </CollapsibleContent>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MatchToJobCard
// ---------------------------------------------------------------------------

export interface MatchToJobCardProps {
  profileMatchingSummary?: string;
}

export function MatchToJobCard({
  profileMatchingSummary,
}: MatchToJobCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const explanation = profileMatchingSummary || "";

  const handleCopy = useCallback(async () => {
    if (!explanation) return;
    try {
      await navigator.clipboard.writeText(explanation);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  }, [explanation]);

  // Show expand/collapse toggle for longer content
  const needsTruncation = explanation.length > 200;

  return (
    <div className="rounded-[20px] border border-black-50 bg-white p-5 motion-safe:transition-shadow hover:shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AiSparkleIcon className="size-5" />
          <h4 className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-body-xl font-semibold text-transparent">
            Match to Job Description
          </h4>
        </div>
        {explanation && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy match summary"
            className="flex items-center gap-1 rounded-full px-2 py-1 text-body-sm text-grey motion-safe:transition-colors hover:bg-grey-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <Icon icon={CopyLinear} size="sm" />
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
        )}
      </div>

      {/* Body */}
      {explanation ? (
        <>
          <MarkdownContent
            content={explanation}
            className={`mt-3 overflow-hidden text-body-md font-normal leading-[24px] tracking-[0.07px] text-black-500 motion-safe:transition-[max-height] motion-safe:duration-300 motion-safe:ease-in-out [&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:text-body-md [&_h1]:font-bold [&_h1]:leading-[24px] [&_h1]:first:mt-0 [&_h2]:mb-1.5 [&_h2]:mt-2.5 [&_h2]:text-body-md [&_h2]:font-semibold [&_h2]:leading-[24px] [&_h2]:first:mt-0 [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-body-md [&_h3]:font-semibold [&_h3]:leading-[24px] [&_h3]:first:mt-0 [&_li]:text-body-md [&_li]:leading-[24px] [&_p]:mb-2 [&_p]:text-body-md [&_p]:leading-[24px] [&_p]:last:mb-0 ${needsTruncation && !isExpanded ? "max-h-[96px]" : "max-h-[2000px]"}`}
          />

          {/* See more / See less */}
          {needsTruncation && (
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="mt-2 flex cursor-pointer items-center gap-1 text-body-sm text-grey hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <span>{isExpanded ? "See less" : "See more"}</span>
              <ChevronIcon open={isExpanded} />
            </button>
          )}
        </>
      ) : (
        <p className="mt-3 text-body-sm text-grey">
          No match description available yet.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkExperienceCard
// ---------------------------------------------------------------------------

export interface WorkExperienceCardProps {
  items: EmploymentHistoryItem[];
}

export function WorkExperienceCard({ items }: WorkExperienceCardProps) {
  const sorted = useMemo(() => sortEmploymentByDate(items), [items]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <div className="rounded-[20px] border border-black-50 bg-white p-5 motion-safe:transition-shadow hover:shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-body-xl font-semibold text-secondary">
          Work Experience
        </h4>
        {/* "Detail" button — hidden for now per spec */}
        <span className="opacity-0" aria-hidden="true">
          Detail
        </span>
      </div>

      {items.length > 0 ? (
        <div className="mt-4 flex flex-col gap-2.5">
          {sorted.map((item, index) => {
            const dateRange = item.is_current
              ? `${formatDate(item.start_date)} - Present`
              : item.end_date
                ? `${formatDate(item.start_date)} - ${formatDate(item.end_date)}`
                : formatDate(item.start_date);

            const isOpen = expandedIds.has(item.id);
            const hasDescription = !!item.description;
            const hasAchievements =
              item.achievements && item.achievements.length > 0;
            const hasExpandableContent = hasDescription || hasAchievements;

            return (
              <div
                key={item.id}
                className={`overflow-hidden bg-bg-page ${
                  index === 0 ? "rounded-xl" : "rounded-lg"
                }`}
              >
                {/* Clickable header */}
                <button
                  type="button"
                  onClick={() => toggleExpanded(item.id)}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center gap-3 px-[17px] py-[13px] text-left motion-safe:transition-colors hover:bg-grey-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                >
                  {/* Company logo placeholder */}
                  <div
                    className="flex size-6 shrink-0 items-center justify-center rounded-md bg-grey-50"
                    aria-hidden="true"
                  >
                    <Icon
                      icon={SuitcaseLinear}
                      size="xs"
                      className="text-grey"
                    />
                  </div>

                  {/* Text content */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body-md font-semibold text-black-500">
                      {item.job_title ? toTitleCase(item.job_title) : ""}
                    </p>
                    <p className="truncate text-body-md text-black-500/60">
                      {item.company_name ? toTitleCase(item.company_name) : ""}
                    </p>
                  </div>

                  {/* Date range */}
                  <span className="shrink-0 whitespace-nowrap text-body-md font-medium text-black-500">
                    {dateRange}
                  </span>

                  {/* Expand/collapse chevron */}
                  <span className="shrink-0 text-grey">
                    <ChevronIcon open={isOpen} />
                  </span>
                </button>

                {/* Expandable content */}
                <CollapsibleContent open={isOpen}>
                  <div className="border-t border-grey-100 px-[17px] pb-[13px] pt-3">
                    {hasExpandableContent ? (
                      <div className="flex flex-col gap-3">
                        {hasDescription && (
                          <MarkdownContent
                            content={item.description!}
                            className="text-body-sm leading-relaxed text-black-500/80 [&_p]:mb-1.5 [&_p]:last:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:text-body-sm"
                          />
                        )}
                        {hasAchievements && (
                          <ul className="ml-4 list-disc space-y-1">
                            {item.achievements.map(
                              (achievement, achievementIndex) => (
                                <li
                                  key={achievementIndex}
                                  className="text-body-sm leading-relaxed text-black-500/80"
                                >
                                  {achievement}
                                </li>
                              )
                            )}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <p className="text-body-sm text-grey">
                        No description available.
                      </p>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-3 text-body-sm text-grey">
          No work experience data available.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EducationRow + EducationCard
// ---------------------------------------------------------------------------

function EducationRow({
  item,
  isFirst,
}: {
  item: EducationItem;
  isFirst: boolean;
}) {
  const year = item.end_date
    ? formatDate(item.end_date)
    : item.start_date
      ? formatDate(item.start_date)
      : null;

  return (
    <div
      className={`flex items-center gap-3 bg-bg-page px-[17px] py-[13px] ${
        isFirst ? "rounded-xl" : "rounded-lg"
      }`}
    >
      <div
        className="flex size-6 shrink-0 items-center justify-center rounded-md bg-grey-50"
        aria-hidden="true"
      >
        <GraduationIcon />
      </div>
      <div className="min-w-0 flex-1">
        {item.institution_name && (
          <p className="truncate text-body-md font-semibold text-black-500">
            {toTitleCase(item.institution_name)}
          </p>
        )}
        {(item.degree || item.field_of_study) && (
          <p className="truncate text-body-md text-black-500/60">
            {[
              item.degree ? toTitleCase(item.degree) : null,
              item.field_of_study ? toTitleCase(item.field_of_study) : null,
            ]
              .filter(Boolean)
              .join(" in ")}
          </p>
        )}
      </div>
      {year && (
        <span className="shrink-0 whitespace-nowrap text-body-md font-medium text-black-500">
          {year}
        </span>
      )}
    </div>
  );
}

const EDUCATION_VISIBLE_COUNT = 3;

export interface EducationCardProps {
  items: EducationItem[];
}

export function EducationCard({ items }: EducationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasOverflow = items.length > EDUCATION_VISIBLE_COUNT;
  const visibleItems = items.slice(0, EDUCATION_VISIBLE_COUNT);
  const overflowItems = items.slice(EDUCATION_VISIBLE_COUNT);

  return (
    <div className="rounded-[20px] border border-black-50 bg-white p-5 motion-safe:transition-shadow hover:shadow-sm">
      {/* Header */}
      <h4 className="flex items-center gap-2 text-body-xl font-semibold text-secondary">
        <GraduationIcon />
        Education
      </h4>

      {items.length > 0 ? (
        <>
          <div className="mt-4 flex flex-col gap-2.5">
            {visibleItems.map((item, index) => (
              <EducationRow key={item.id} item={item} isFirst={index === 0} />
            ))}
          </div>

          {hasOverflow && (
            <>
              <CollapsibleContent open={isExpanded}>
                <div className="mt-2.5 flex flex-col gap-2.5">
                  {overflowItems.map((item) => (
                    <EducationRow key={item.id} item={item} isFirst={false} />
                  ))}
                </div>
              </CollapsibleContent>

              <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="mt-3 flex cursor-pointer items-center gap-1 text-body-sm text-grey hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <span>{isExpanded ? "See less" : "See more"}</span>
                <ChevronIcon open={isExpanded} />
              </button>
            </>
          )}
        </>
      ) : (
        <p className="mt-3 text-body-sm text-grey">
          No education data available.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CvViewer — Resume tab content shared between modal and profile page
// ---------------------------------------------------------------------------

export interface CvViewerProps {
  candidateId: string;
  candidateName?: string;
}

// Zoom constants
const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;
const SCALE_STEP = 0.15;

export function CvViewer({ candidateId, candidateName }: CvViewerProps) {
  const { blobUrl, contentType, isLoading, isError, download } =
    useCandidateCv(candidateId);

  const isPdf = contentType?.includes("pdf") ?? false;
  const isDocx =
    contentType?.includes("wordprocessingml") ||
    contentType?.includes("vnd.openxmlformats") ||
    contentType?.includes("msword");

  const displayName = candidateName ? `${candidateName} Resume` : "Resume";

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  // currentPage is derived from scroll position — which page is most visible
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);

  // Ref to the scrollable container holding all pages
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Map of page number → wrapper div for scroll-based page detection
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  function handleDocumentLoadSuccess({ numPages: n }: { numPages: number }) {
    setNumPages(n);
    setCurrentPage(1);
  }

  function zoomIn() {
    setScale((prev) =>
      parseFloat(Math.min(prev + SCALE_STEP, MAX_SCALE).toFixed(2))
    );
  }

  function zoomOut() {
    setScale((prev) =>
      parseFloat(Math.max(prev - SCALE_STEP, MIN_SCALE).toFixed(2))
    );
  }

  // Scroll a specific page into view (used by bottom prev/next arrows)
  function scrollToPage(page: number) {
    if (page < 1 || page > numPages) return;
    const el = pageRefs.current.get(page);
    if (el && scrollContainerRef.current) {
      const containerTop =
        scrollContainerRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const offset =
        elTop - containerTop + scrollContainerRef.current.scrollTop;
      scrollContainerRef.current.scrollTo({ top: offset, behavior: "smooth" });
    }
  }

  // Scroll handler: determine which page occupies the most visible area
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages === 0) return;

    const containerRect = container.getBoundingClientRect();
    let bestPage = 1;
    let bestVisibleHeight = -1;

    pageRefs.current.forEach((el, pageNum) => {
      const rect = el.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, containerRect.top);
      const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      if (visibleHeight > bestVisibleHeight) {
        bestVisibleHeight = visibleHeight;
        bestPage = pageNum;
      }
    });

    setCurrentPage(bestPage);
  }, [numPages]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Format scale label: e.g. 1.0 → "1.0x", 1.5 → "1.5x"
  const scaleLabel = `${scale.toFixed(1)}x`;

  return (
    <div className="flex flex-col gap-0 rounded-3xl bg-white shadow-sm">
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <p className="text-sm font-medium text-text-primary">{displayName}</p>
        <button
          type="button"
          disabled={!blobUrl || isLoading}
          onClick={() => download(candidateName)}
          aria-label="Download CV"
          className={cn(
            "flex items-center gap-1.5 rounded-full border border-grey-100 px-4 py-3 text-sm font-bold text-grey motion-safe:transition-[background-color,opacity]",
            !blobUrl || isLoading
              ? "cursor-not-allowed opacity-40"
              : "cursor-pointer hover:bg-grey-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-[0.97]"
          )}
        >
          <Icon icon={DownloadMinimalisticLinear} size="sm" />
          Download CV
        </button>
      </div>

      {/* Content */}
      <div className="min-h-[480px]">
        {isLoading && (
          <div
            className="flex h-full min-h-[480px] items-center justify-center px-5 pb-5"
            aria-label="Loading CV"
          >
            <GradientSpinner size={32} />
          </div>
        )}

        {!isLoading && isError && (
          <div className="flex h-full min-h-[480px] flex-col items-center justify-center gap-2 px-5 pb-5">
            <p className="text-body-md font-medium text-grey">
              No resume available
            </p>
          </div>
        )}

        {!isLoading && !isError && blobUrl && isPdf && (
          <div className="flex flex-col overflow-hidden rounded-b-3xl">
            {/* Viewer body: left zoom strip + scrollable pages */}
            <div className="flex h-[680px] bg-[#e1e4ea]">
              {/* Left zoom strip — 28px wide, 4px gap between items */}
              <div className="flex w-7 shrink-0 flex-col items-center gap-1 py-3 ml-5">
                {/* Zoom out button — white bordered box */}
                <button
                  type="button"
                  onClick={zoomOut}
                  disabled={scale <= MIN_SCALE}
                  aria-label="Zoom out"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-[4px] border border-[#cacfd8] bg-white text-grey shadow-sm transition-colors",
                    scale <= MIN_SCALE
                      ? "cursor-not-allowed opacity-40"
                      : "cursor-pointer hover:bg-grey-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                  )}
                >
                  <Icon icon={MagnifierZoomOutLinear} size="sm" />
                </button>

                {/* Grey bar with zoom in control inside */}
                <div className="flex w-7 flex-col items-center justify-center gap-1 rounded-[4px] bg-[#cacfd8] py-2">
                  <button
                    type="button"
                    onClick={zoomIn}
                    disabled={scale >= MAX_SCALE}
                    aria-label="Zoom in"
                    className={cn(
                      "flex size-5 items-center justify-center rounded-sm text-[#2c2f36] transition-colors",
                      scale >= MAX_SCALE
                        ? "cursor-not-allowed opacity-40"
                        : "cursor-pointer hover:bg-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                    )}
                  >
                    <Icon icon={MagnifierZoomInLinear} size="sm" />
                  </button>
                </div>

                {/* Zoom level label — white bordered box */}
                <div className="flex h-7 w-7 items-center justify-center rounded-[4px] border border-[#cacfd8] bg-white">
                  <span className="text-[9px] font-semibold tabular-nums text-grey">
                    {scaleLabel}
                  </span>
                </div>
              </div>

              {/* Scrollable all-pages area */}
              <div
                ref={scrollContainerRef}
                className="flex flex-1 overflow-auto"
                aria-label={`Page ${currentPage} of ${numPages}`}
              >
                <div className="flex min-w-full flex-col items-center py-6 px-4 gap-4">
                  <Document
                    file={blobUrl}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={
                      <div className="flex h-64 items-center justify-center">
                        <GradientSpinner size={32} />
                      </div>
                    }
                    error={
                      <div className="flex h-64 items-center justify-center">
                        <p className="text-sm text-grey">Failed to load PDF.</p>
                      </div>
                    }
                  >
                    {numPages > 0 &&
                      Array.from({ length: numPages }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <div
                            key={pageNum}
                            ref={(el) => {
                              if (el) pageRefs.current.set(pageNum, el);
                              else pageRefs.current.delete(pageNum);
                            }}
                            className="border border-[#cacfd8] shadow-[0px_0px_55.3px_0px_rgba(14,18,27,0.1)]"
                          >
                            <Page
                              pageNumber={pageNum}
                              scale={scale}
                              renderTextLayer
                              renderAnnotationLayer
                            />
                          </div>
                        );
                      })}
                  </Document>
                </div>
              </div>
            </div>

            {/* Bottom page indicator */}
            <div className="flex items-center justify-center bg-[#e1e4ea] py-2">
              <div className="flex items-center gap-1 rounded-full bg-[#2c2f36] px-3 py-1.5">
                <button
                  type="button"
                  onClick={() => scrollToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-white/80 transition-colors",
                    currentPage <= 1
                      ? "cursor-not-allowed opacity-40"
                      : "hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                  )}
                >
                  <Icon icon={AltArrowLeftLinear} size="sm" />
                </button>
                <span className="min-w-[64px] text-center text-xs font-medium text-white tabular-nums">
                  {numPages > 0 ? `${currentPage} / ${numPages}` : "— / —"}
                </span>
                <button
                  type="button"
                  onClick={() => scrollToPage(currentPage + 1)}
                  disabled={currentPage >= numPages}
                  aria-label="Next page"
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-white/80 transition-colors",
                    currentPage >= numPages
                      ? "cursor-not-allowed opacity-40"
                      : "hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                  )}
                >
                  <Icon icon={AltArrowRightLinear} size="sm" />
                </button>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !isError && blobUrl && isDocx && (
          <div className="flex h-full min-h-[480px] flex-col items-center justify-center gap-4 px-5 pb-5">
            <p className="text-body-md text-grey">
              Preview not available for this file format.
            </p>
            <button
              type="button"
              onClick={() => download(candidateName)}
              className="flex cursor-pointer items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-body-md font-semibold text-white motion-safe:transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
            >
              <Icon icon={DownloadMinimalisticLinear} size="sm" />
              Download to view
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
